import React, { useState, useMemo, useEffect } from 'react';
import { Calculator, Euro, Calendar, Percent, ShieldCheck, ArrowRight } from 'lucide-react';
import { formatPrice, AGENT_INFO } from '../constants';
import Button from './ui/Button';

const parsePriceNumber = (val) => {
    if (typeof val === 'number') return isNaN(val) || val <= 0 ? 250000 : val;
    if (!val) return 250000;
    const cleaned = Number(String(val).replace(/[^0-9]/g, ''));
    return isNaN(cleaned) || cleaned <= 0 ? 250000 : cleaned;
};

const MortgageCalculator = ({ propertyPrice = 250000, propertyTitle = '', propertyRef = '', agentPhone }) => {
    const initialPrice = useMemo(() => parsePriceNumber(propertyPrice), [propertyPrice]);

    // Form states
    const [deposit, setDeposit] = useState(() => Math.round(initialPrice * 0.10));
    const [durationYears, setDurationYears] = useState(20);
    const [interestRate, setInterestRate] = useState(3.6);
    const [insuranceRate, setInsuranceRate] = useState(0.36);
    const [isNotaryOld, setIsNotaryOld] = useState(true);

    useEffect(() => {
        setDeposit(Math.round(initialPrice * 0.10));
    }, [initialPrice]);

    // Calculated values
    const notaryFees = useMemo(() => {
        const rate = isNotaryOld ? 0.075 : 0.025;
        return Math.round(initialPrice * rate);
    }, [initialPrice, isNotaryOld]);

    const totalProjectCost = useMemo(() => {
        return initialPrice + notaryFees;
    }, [initialPrice, notaryFees]);

    const loanAmount = useMemo(() => {
        const amount = totalProjectCost - deposit;
        return amount > 0 ? amount : 0;
    }, [totalProjectCost, deposit]);

    const calculations = useMemo(() => {
        const months = durationYears * 12;
        const monthlyInterestRate = (interestRate / 100) / 12;

        let monthlyPayment = 0;
        if (monthlyInterestRate > 0 && months > 0 && loanAmount > 0) {
            monthlyPayment = (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, months)) / (Math.pow(1 + monthlyInterestRate, months) - 1);
        } else if (months > 0 && loanAmount > 0) {
            monthlyPayment = loanAmount / months;
        }

        const monthlyInsurance = (loanAmount * (insuranceRate / 100)) / 12;
        const totalMonthlyPayment = Math.round(monthlyPayment + monthlyInsurance);

        const totalCostOfCredit = Math.round((monthlyPayment * months) + (monthlyInsurance * months) - loanAmount);

        return {
            monthlyPayment: Math.round(monthlyPayment),
            monthlyInsurance: Math.round(monthlyInsurance),
            totalMonthlyPayment: isNaN(totalMonthlyPayment) ? 0 : totalMonthlyPayment,
            totalCostOfCredit: isNaN(totalCostOfCredit) || totalCostOfCredit < 0 ? 0 : totalCostOfCredit,
            months
        };
    }, [loanAmount, durationYears, interestRate, insuranceRate]);

    const handleContactAgent = () => {
        let phone = (agentPhone || AGENT_INFO?.phone)?.replace(/[^0-9]/g, '') || '';
        // Convert French number to international format (add +33 if not present)
        if (phone.startsWith('0')) {
            phone = '33' + phone.substring(1);
        } else if (!phone.startsWith('33')) {
            phone = '33' + phone;
        }
        const message = encodeURIComponent(
            `Bonjour,\n\nJe suis intéressé(e) par le bien "${propertyTitle}" (${formatPrice(initialPrice)}).\n` +
            `J'ai effectué une simulation d'emprunt sur votre site :\n` +
            `- Apport : ${formatPrice(deposit)}\n` +
            `- Mensualité estimée : ${calculations.totalMonthlyPayment} €/mois sur ${durationYears} ans (taux ${interestRate}%).\n\n` +
            `Pouvez-vous me recontacter pour échanger sur ce bien ?`
        );
        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    };

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden my-12">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-[#002B5B] to-[#001B3B] p-6 sm:p-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-xs font-semibold tracking-wide text-blue-200 mb-2">
                        <Calculator size={14} />
                        Simulateur de Financement
                    </div>
                    <h3 className="text-xl sm:text-2xl font-black">Estimez vos mensualités pour ce bien</h3>
                    <p className="text-xs text-white/70 mt-1">Calculez rapidement votre projet d'achat et vos mensualités estimées.</p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl px-5 py-3 border border-white/10 text-right w-full sm:w-auto">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-white/60">Prix du bien</p>
                    <p className="text-2xl font-black text-white">{formatPrice(initialPrice)}</p>
                </div>
            </div>

            <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Sliders Form Controls */}
                <div className="lg:col-span-7 space-y-6">
                    {/* Apport Personnel */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5">
                                <Euro size={14} className="text-[#002B5B]" /> Apport personnel
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    value={deposit}
                                    onChange={(e) => setDeposit(Math.max(0, Number(e.target.value)))}
                                    className="w-28 text-right font-black text-sm text-[#002B5B] bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-[#002B5B]"
                                />
                                <span className="text-xs font-semibold text-gray-400">€ ({Math.round((deposit / (initialPrice || 1)) * 100)}%)</span>
                            </div>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max={initialPrice}
                            step="5000"
                            value={deposit}
                            onChange={(e) => setDeposit(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#002B5B]"
                        />
                    </div>

                    {/* Durée du Prêt */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5">
                                <Calendar size={14} className="text-[#002B5B]" /> Durée de l'emprunt
                            </label>
                            <span className="font-black text-sm text-[#002B5B] bg-blue-50 px-3 py-1 rounded-lg border border-blue-100">
                                {durationYears} ans ({durationYears * 12} mois)
                            </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mb-2">
                            {[10, 15, 20, 25].map((years) => (
                                <button
                                    key={years}
                                    type="button"
                                    onClick={() => setDurationYears(years)}
                                    className={`py-2 rounded-xl text-xs font-bold border transition-all ${durationYears === years ? 'bg-[#002B5B] text-white border-[#002B5B] shadow-md' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`}
                                >
                                    {years} ans
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Taux d'intérêt & Assurance */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1">
                                    <Percent size={13} className="text-[#002B5B]" /> Taux d'intérêt
                                </label>
                                <span className="font-bold text-xs text-[#002B5B]">{interestRate} %</span>
                            </div>
                            <input
                                type="number"
                                step="0.1"
                                min="0.5"
                                max="10"
                                value={interestRate}
                                onChange={(e) => setInterestRate(Number(e.target.value))}
                                className="w-full font-semibold text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002B5B]"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1">
                                    <ShieldCheck size={13} className="text-[#002B5B]" /> Assurance
                                </label>
                                <span className="font-bold text-xs text-[#002B5B]">{insuranceRate} %</span>
                            </div>
                            <input
                                type="number"
                                step="0.05"
                                min="0.05"
                                max="2"
                                value={insuranceRate}
                                onChange={(e) => setInsuranceRate(Number(e.target.value))}
                                className="w-full font-semibold text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002B5B]"
                            />
                        </div>
                    </div>

                    {/* Type de bien (frais de notaire) */}
                    <div className="pt-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-2">Frais de notaire estimés</label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setIsNotaryOld(true)}
                                className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold border flex items-center justify-between ${isNotaryOld ? 'bg-[#002B5B]/5 border-[#002B5B] text-[#002B5B]' : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                            >
                                <span>Ancien (~7.5%)</span>
                                <span className="font-black">{formatPrice(Math.round(initialPrice * 0.075))}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsNotaryOld(false)}
                                className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold border flex items-center justify-between ${!isNotaryOld ? 'bg-[#002B5B]/5 border-[#002B5B] text-[#002B5B]' : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                            >
                                <span>Neuf (~2.5%)</span>
                                <span className="font-black">{formatPrice(Math.round(initialPrice * 0.025))}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Calculation Output Card */}
                <div className="lg:col-span-5 bg-gray-50 rounded-2xl p-6 border border-gray-200 flex flex-col justify-between">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Estimation de votre mensualité</p>
                        <div className="flex items-baseline gap-1 my-2">
                            <span className="text-4xl sm:text-5xl font-black text-[#002B5B]">{calculations.totalMonthlyPayment}</span>
                            <span className="text-xl font-bold text-[#002B5B]">€ / mois</span>
                        </div>

                        <p className="text-xs text-gray-500 mb-6">
                            dont <span className="font-semibold text-gray-700">{calculations.monthlyPayment} €</span> de remboursement + <span className="font-semibold text-gray-700">{calculations.monthlyInsurance} €</span> d'assurance.
                        </p>

                        <div className="space-y-3 pt-4 border-t border-gray-200 text-xs">
                            <div className="flex justify-between text-gray-600">
                                <span>Montant à emprunter :</span>
                                <span className="font-bold text-gray-900">{formatPrice(loanAmount)}</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <span>Frais de notaire inclus :</span>
                                <span className="font-bold text-gray-900">{formatPrice(notaryFees)}</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <span>Coût total du crédit :</span>
                                <span className="font-bold text-gray-900">{formatPrice(calculations.totalCostOfCredit)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 space-y-3">
                        <Button
                            onClick={handleContactAgent}
                            className="w-full bg-[#002B5B] hover:bg-[#001B3B] text-white font-bold py-3 text-xs sm:text-sm rounded-xl shadow-lg flex items-center justify-center gap-2"
                        >
                            <span>Valider mon projet avec notre équipe</span>
                            <ArrowRight size={16} />
                        </Button>
                        <p className="text-[10px] text-gray-400 text-center leading-tight">
                            * Simulation indicative non contractuelle basée sur un taux hors assurance de {interestRate}%.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MortgageCalculator;
